import { Inject } from '@odinjs/odin';

import Controller, { OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HttpResponse, HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { FieldAPI, FormAPI, InputAPI, PanelAPI } from '@philips-internal/oswald-ui-components/lib/components';

import { Endpoint } from '../../../../../constants/endpoints';
import { FragmentNames, NutritionOralDietViewFragmentAliases } from '../../../../../codegen';
import BaseController from '../../../../../controllers/BaseController';
import InformationBarUtils from '../../../../../controllers/commons/header/information-bar-header-utils';
import { OralDietTypes } from './oral-diet-types';
import moment from 'moment';
import { ISO_DATE_INPUT } from '../../../../../constants/date-utils';
import { DurationType } from '../../cpoe-view-types';

@Controller({ fragment: FragmentNames.NUTRITION_ORAL_DIET_VIEW_FRAGMENT })
class OralDietViewController extends BaseController {

  @Inject(NutritionOralDietViewFragmentAliases.PANEL_LOADER)
  panelLoaderAPI!: PanelAPI;

  @Inject(NutritionOralDietViewFragmentAliases.PANEL_CONTENT)
  panelContentAPI!: PanelAPI;

  @Inject(NutritionOralDietViewFragmentAliases.FORM_MAIN_INFORMATIONS)
  formMainInformationsAPI!: FormAPI;

  @Inject(NutritionOralDietViewFragmentAliases.FORM_PERIOD_OF_VALIDITY)
  formPeriodValidityAPI!: FormAPI;

  @Inject(NutritionOralDietViewFragmentAliases.FORM_ADDITIONAL_INFORMATION)
  formAddicionalInformationsAPI!: FormAPI;

  @Inject(NutritionOralDietViewFragmentAliases.FIELD_JUSTIFICATION)
  fieldJustificationAPI!: FieldAPI;

  @Inject(NutritionOralDietViewFragmentAliases.FIELD_OBSERVATION)
  fieldObservationAPI!: FieldAPI;

  @Inject(NutritionOralDietViewFragmentAliases.FIELD_PRESCRIBER_DATE)
  fieldPrescriberDtAPI!: FieldAPI;

  @Inject(NutritionOralDietViewFragmentAliases.FIELD_NUTRITIONAL_CALORIC_PROFILE)
  fieldNutritionalCaloricProfileAPI!: FieldAPI;

  @Inject(NutritionOralDietViewFragmentAliases.FIELD_PRESCRIBER_NAME)
  fieldPrescriberNameAPI!: FieldAPI;

  @Inject(NutritionOralDietViewFragmentAliases.FIELD_DIET_INFORMATION)
  fieldDietInformationAPI!: FieldAPI;

  @Inject(NutritionOralDietViewFragmentAliases.FIELD_END_DATE)
  fieldEndDateAPI!: FieldAPI;

  @Inject(NutritionOralDietViewFragmentAliases.INPUT_START_DATE)
  inputStartDateAPI!: InputAPI;

  @Inject(NutritionOralDietViewFragmentAliases.INPUT_END_DATE)
  inputEndDateAPI!: InputAPI;

  @OnMount(NutritionOralDietViewFragmentAliases.PANEL_CONTENT)
  handleMountPanelCard(): void {
    InformationBarUtils.collapsedBar = true;
    const { nutritionId, patientId } = this.historyService.getCurrentRouteParams()
    this.fetchOralDietDetails(String(nutritionId), String(patientId))
  }

  @OnMount(NutritionOralDietViewFragmentAliases.FORM_MAIN_INFORMATIONS)
  handleMountFormInformations(): void {
    const oralDietView = this.context.get('oralDietView') as OralDietTypes
    if (oralDietView) {
      const {
        diet,
        interval,
        nutritionalCalorieProfile,
        information
      } = oralDietView;

      this.formMainInformationsAPI.setValue({
        diet,
        information,
        interval,
        nutritionalCalorieProfile,
      })

      this.fieldNutritionalCaloricProfileAPI.setVisible(!!nutritionalCalorieProfile)
      this.fieldDietInformationAPI.setVisible(!!information)
    }
  }

  @OnMount(NutritionOralDietViewFragmentAliases.FORM_PERIOD_OF_VALIDITY)
  handleMountFormPeriodOfValidity(): void {
    const oralDietView = this.context.get('oralDietView') as OralDietTypes

    if (oralDietView) {
      const { duration, hours, durationType } = oralDietView
      const startDate = moment(String(oralDietView.startDate)).utc().format(ISO_DATE_INPUT)
      const endDate = moment(String(oralDietView.endDate)).utc().format(ISO_DATE_INPUT)

      this.formPeriodValidityAPI.setValue({
        duration,
        endDate,
        hours,
        startDate,
      })

      this.inputStartDateAPI.setValue(startDate)
      this.inputEndDateAPI.setValue(endDate)

      this.showFieldsByDurationType(durationType)
    }
  }

  private showFieldsByDurationType(duration: string): void {
    this.fieldEndDateAPI.setVisible(duration === DurationType.PROGRAMMED)
  }

  @OnMount(NutritionOralDietViewFragmentAliases.FORM_ADDITIONAL_INFORMATION)
  handleMountFormAddicionalInformations(): void {
    const oralDietView = this.context.get('oralDietView') as OralDietTypes
    if (oralDietView) {
      const { prescriberDt, prescriberName, justification, observation } = oralDietView

      this.formAddicionalInformationsAPI.setValue({
        justification,
        observation,
        prescriberDt,
        prescriberName,
      })

      this.fieldJustificationAPI.setVisible(!!justification)
      this.fieldObservationAPI.setVisible(!!observation)
      this.fieldPrescriberNameAPI.setVisible(!!prescriberName)
      this.fieldPrescriberDtAPI.setVisible(!!prescriberDt)
    }
  }

  private fetchOralDietDetails(nutritionId: string, patientId: string): void {
    this.panelLoaderAPI.setVisible(true)
    this.panelContentAPI.setVisible(false)
    HttpService.get(Endpoint.GET_CPOE_NUTRITION_ORAL_DIET_DETAILS, { nutritionId, patientId })
      .then((response: HttpResponse) => {
        const oralDietView = response.body as OralDietTypes;
        this.context.set({ oralDietView })
      }).catch((err) => {
        this.showToast(err?.body, ToastLevel.ERROR)
      }).finally(() => {
        this.panelLoaderAPI.setVisible(false)
        this.panelContentAPI.setVisible(true)
      })
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'nutrition-water-balance',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }
}

export default OralDietViewController;